import { CircularProgress } from '@mui/material';
import moment from 'moment';
import { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  StoreRequest,
  StoreRequestBundle,
} from '../../../../shared/api/hscan/hospital';
import { useStoreFromsInBundle } from '../../../../shared/hooks/hospitalHook';
import expandDown from '../../assets/icons/icon_expand-down.svg';
import expandRight from '../../assets/icons/icon_expand-right.svg';
import { IconButton } from '../../common/components/Icon';
import {
  MobilePaginationWithoutRowsPerPage,
  MobileRowsPerPage,
  MobileTable,
  MobileTableFullRow,
  MobileTableRow,
  MobileTableSubFullRow,
  MobileTableWrapper,
  RowContainer,
} from '../../common/components/MobileTable';
import { TableAndElementInformation } from '../../common/components/SortableTable/PresentationalTable';
import { useModalityLabel } from '../../hooks/modalityHook';

import { StatusMessage } from './StatusMessage';
import {
  BundleTableContext,
  BundleTableElementContext,
  BundleTableElementInformationProvider,
  IconButtonWrapper,
  StoreRequestsTableProps,
} from './UploadedImageListTable';

interface MobileUploadedImageListTableElementProps
  extends TableAndElementInformation<StoreRequestBundle, 'id'> {
  onClickStatus: (storeRequest: StoreRequest) => void;
}
export const MobileUploadedImageListTableElement = (
  props: MobileUploadedImageListTableElementProps,
) => {
  const { element, onClickStatus } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.uploadedImages.table',
  });
  const [showDetail, setShowDetail] = useState(false);

  return (
    <MobileTable>
      <tbody>
        <MobileTableRow label={`${t('header.id')}`}>
          {element.id ?? ''}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.requestDate')}`}>
          {moment(element.createdAt).format('YYYY.MM.DD HH:mm:ss')}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.hospitalName')}`}>
          {element.hospitalName ?? ''}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.numImages')}`}>
          {element.requestIds.length ?? ''}
        </MobileTableRow>
        <MobileTableFullRow
          onClick={() => setShowDetail(show => !show)}
          label={
            showDetail ? (
              <ButtonContent>
                {t('mobile.fold')}
                <IconButtonWrapper>
                  <IconButton
                    imageUrl={expandDown}
                    width={'14px'}
                    height={'8px'}
                    color="#666973"
                  />
                </IconButtonWrapper>
              </ButtonContent>
            ) : (
              <ButtonContent>
                {t('mobile.expand')}
                <IconButtonWrapper>
                  <IconButton
                    imageUrl={expandRight}
                    width={'8px'}
                    height={'14px'}
                    color="#666973"
                  />
                </IconButtonWrapper>
              </ButtonContent>
            )
          }
        />
        {showDetail && (
          <MobileStoreRequestsTable
            bundle={element}
            onClickStatus={onClickStatus}
          />
        )}
      </tbody>
    </MobileTable>
  );
};

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
`;

const MobileStoreRequestsTable = ({
  bundle,
  onClickStatus,
}: StoreRequestsTableProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.uploadedImages.table',
  });
  const modalityLabel = useModalityLabel();

  const { data: storeRequests, isFetching } = useStoreFromsInBundle({
    bundleId: bundle.id,
  });

  return isFetching ? (
    <RowContainer>
      <td colSpan={2} align="center">
        <CircularProgress />
      </td>
    </RowContainer>
  ) : storeRequests ? (
    <>
      {storeRequests.map((storeRequest, index) => (
        <Fragment key={storeRequest.id}>
          <MobileTableSubFullRow label={`${index + 1}.`} />
          <MobileTableRow label={`${t('header.imageDate')}`}>
            {moment(storeRequest.studyDate).format('YYYY.MM.DD')}
          </MobileTableRow>
          <MobileTableRow label={t('header.hospitalNameInStore')}>
            {storeRequest.hospitalName}
          </MobileTableRow>
          <MobileTableRow label={`${t('header.modalities')}`}>
            {storeRequest.modalities
              ?.map(modality => modalityLabel(modality))
              .join(', ')}
          </MobileTableRow>
          <MobileTableRow label={`${t('header.bodyPart')}`}>
            {storeRequest.studyDescription ?? ''}
          </MobileTableRow>
          <MobileTableRow
            label={`${t('header.status')}`}
            onClick={() => onClickStatus(storeRequest)}>
            <StatusMessage status={storeRequest.status} />
          </MobileTableRow>
        </Fragment>
      ))}
    </>
  ) : null;
};

interface MobileUploadedImageListTableProps {
  onClickStatus: (element: StoreRequest) => void;
}
export const MobileUploadedImageListTable = ({
  onClickStatus,
}: MobileUploadedImageListTableProps) => {
  const Element = useCallback(
    (props: TableAndElementInformation<StoreRequestBundle, 'id'>) => (
      <MobileUploadedImageListTableElement
        {...props}
        onClickStatus={onClickStatus}
      />
    ),
    [onClickStatus],
  );
  return (
    <MobileTableWrapper
      tableContext={BundleTableContext}
      elementContext={BundleTableElementContext}
      ElementProvider={BundleTableElementInformationProvider}
      Element={Element}
      Header={MobileRowsPerPage}
      Footer={MobilePaginationWithoutRowsPerPage}
    />
  );
};
